import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { setLoading } from 'Actions/loading';
import { Box, Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, Typography } from '@mui/material';
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined';
import FolderOffOutlinedIcon from '@mui/icons-material/FolderOffOutlined';
import { AddIcon } from 'Shared/Icons';
import { useSnackbar } from 'notistack';
import { CustomSnackbar } from 'Shared/CustomSnackbar';
import http from 'Utils/http';
import { cachedGet } from 'Utils/cachedGet';
import { Transition } from 'Shared/Transition';
import { process } from 'Utils/errorHandler';
import CreateFolderModal from 'Shared/flows/folders/CreateFolderModal';
import { buildFolderOptions } from 'Shared/flows/folders/folderUtils';

const SelectFolderModal = ({ open, flowId, onClose }) => {
    const dispatch = useDispatch();
    const { enqueueSnackbar } = useSnackbar();

    const [folders, setFolders] = useState([]);
    const [selected, setSelected] = useState(null);
    const [createOpen, setCreateOpen] = useState(false);
    const [showInRoot, setShowInRoot] = useState(false);

    const fetchFolders = useCallback(() => {
        return cachedGet('/folders').then((res) => setFolders(res.data.folders || []));
    }, []);

    useEffect(() => {
        if (!open) {
            return;
        }
        dispatch(setLoading(true));
        Promise.all([
            cachedGet('/folders'),
            cachedGet(`/flow/${flowId}/folder`),
        ])
            .then(([foldersRes, currentRes]) => {
                setFolders(foldersRes.data.folders || []);
                setSelected(currentRes.data.folder_id ?? null);
                setShowInRoot(false);
            })
            .catch((err) => process(err, enqueueSnackbar))
            .finally(() => dispatch(setLoading(false)));
    }, [open, flowId, dispatch, enqueueSnackbar]);

    const options = buildFolderOptions(folders);

    const save = () => {
        dispatch(setLoading(true));
        http.post(`/flow/${flowId}/folder`, { folder_id: selected, show_in_root: showInRoot })
            .then(() => {
                enqueueSnackbar(selected ? 'Flow moved to folder' : 'Flow removed from folder', {
                    action: CustomSnackbar,
                    variant: 'success',
                });
                onClose();
            })
            .catch((err) => process(err, enqueueSnackbar))
            .finally(() => dispatch(setLoading(false)));
    };

    const Row = ({ id, icon, label, depth = 0 }) => {
        const isSelected = String(selected ?? 'none') === String(id ?? 'none');
        return (
            <Box
                onClick={() => setSelected(id)}
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '10px 12px',
                    paddingLeft: `${12 + depth * 18}px`,
                    borderRadius: '6px',
                    cursor: 'pointer',
                    color: isSelected ? '#289BEF' : '#3B465C',
                    backgroundColor: isSelected ? '#e8f4fd' : 'transparent',
                    '&:hover': { backgroundColor: isSelected ? '#e8f4fd' : '#eef1f4' },
                }}
            >
                {icon}
                <Typography sx={{ fontSize: '14px', color: 'inherit', fontWeight: isSelected ? 600 : 400 }}>
                    {label}
                </Typography>
            </Box>
        );
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            TransitionComponent={Transition}
            sx={{
                '& .MuiDialog-container': { alignItems: 'flex-start', paddingTop: '150px' },
                '& .MuiDialog-paper': { width: '50%', minWidth: '320px', maxWidth: 'none', margin: 0 },
            }}
        >
            <DialogTitle className="h1" variant="h1" sx={{ paddingBottom: '6px', textAlign: 'center', color: '#3B465C' }}>
                Put to Folder
            </DialogTitle>
            <DialogContent>
                <Box sx={{ border: '1px solid #d8e0e8', borderRadius: '8px', maxHeight: '320px', overflow: 'auto', padding: '6px' }}>
                    <Row id={null} icon={<FolderOffOutlinedIcon fontSize="small" sx={{ color: 'inherit' }} />} label="No folder" />
                    {options.map((opt) => (
                        <Row
                            key={opt.id}
                            id={opt.id}
                            depth={opt.depth}
                            icon={<FolderOutlinedIcon fontSize="small" sx={{ color: 'inherit' }} />}
                            label={opt.name}
                        />
                    ))}
                    {!options.length && (
                        <Typography sx={{ fontSize: '13px', color: '#99a1ad', padding: '10px 12px' }}>
                            No folders yet
                        </Typography>
                    )}
                </Box>
                <Button onClick={() => setCreateOpen(true)} startIcon={<AddIcon />} variant="outlined" sx={{ marginTop: '16px' }}>
                    Create Folder
                </Button>
                <FormControlLabel
                    control={<Checkbox checked={showInRoot} onChange={(e) => setShowInRoot(e.target.checked)} />}
                    label="Always show in root"
                    sx={{ display: 'flex', marginTop: '10px', '& .MuiTypography-root': { color: '#3B465C' } }}
                />
            </DialogContent>
            <DialogActions sx={{ padding: '16px 24px' }}>
                <Button onClick={onClose} variant="outlined">Close</Button>
                <Button onClick={save} variant="contained">Save</Button>
            </DialogActions>

            <CreateFolderModal
                open={createOpen}
                onClose={() => setCreateOpen(false)}
                folders={folders}
                onSaved={(folder) => {
                    fetchFolders().then(() => {
                        if (folder?.id) {
                            setSelected(folder.id);
                        }
                    });
                }}
            />
        </Dialog>
    );
};

export default SelectFolderModal;
